/**
 * SupplierPoEmailDialog
 * ---------------------
 * "Send supplier PO" from the Fulfillment Control Center. Picks the awarded
 * supplier, lets the desk confirm PO numbers, line pricing, lead times and the
 * standard shipping instructions, then drops the finished message into Outlook
 * Drafts so the PO + packing slip can be attached and sent.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, ExternalLink, FileText, Plus, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const OPTION_ROWS = [
  { k: "expedite", label: "Important: EXPEDITE SHIPPING Overnight Air if possible" },
  { k: "hospital_po_on_label", label: "Include the hospital PO on the shipping label and packing slip" },
  { k: "include_packing_slip", label: "Please include our packing slip if possible" },
  { k: "omit_invoice", label: "Please do not include your invoice" },
  { k: "allotment_note", label: "Add the separate-allotment / revised packing slip note" },
];

const blankLine = () => ({ code: "", description: "", quantity: "", uom: "", unit_price: "", lead_time: "" });

export const SupplierPoEmailDialog = ({ open, onOpenChange, backorderId, token, onLogged }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !backorderId) return;
    setLoading(true);
    setPreview(null);
    axios.get(`${API}/api/admin/backorders/${backorderId}/supplier-po-email`, { headers })
      .then(({ data }) => { setCtx(data); setIdx(0); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load the PO context"))
      .finally(() => setLoading(false));
  }, [open, backorderId, headers]);

  // Hydrate the editable form whenever the selected supplier changes
  useEffect(() => {
    if (!ctx) return;
    const s = (ctx.suppliers || [])[idx] || {};
    setForm({
      supplier_name: s.supplier_name || "",
      supplier_email: s.supplier_email || "",
      supplier_po_id: s.supplier_po_id || null,
      supplier_po_number: s.supplier_po_number || "",
      greeting_name: s.greeting_name || "",
      to: s.supplier_email || "",
      cc: "",
      hospital_po_number: ctx.hospital_po_number || "",
      sign_off_name: ctx.sign_off_name || "",
      extra_note: "",
      items: (s.items && s.items.length ? s.items : ctx.all_items || []).map((l) => ({
        code: l.code || "", description: l.description || "",
        quantity: l.quantity ?? "", uom: l.uom || "",
        unit_price: l.unit_price ?? "", lead_time: l.lead_time || "",
      })),
      options: { ...(ctx.defaults || {}) },
    });
    setPreview(null);
  }, [ctx, idx]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setOpt = (k, v) => setForm((p) => ({ ...p, options: { ...p.options, [k]: v } }));
  const setLine = (i, k, v) => setForm((p) => ({
    ...p, items: p.items.map((l, j) => (j === i ? { ...l, [k]: v } : l)),
  }));

  const payload = () => ({
    ...form,
    to: form.to.split(",").map((s) => s.trim()).filter(Boolean),
    cc: form.cc.split(",").map((s) => s.trim()).filter(Boolean),
  });

  const buildPreview = async () => {
    if (!form?.supplier_name?.trim()) return toast.error("Pick or name a supplier first");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/backorders/${backorderId}/supplier-po-email/build`,
        payload(), { headers });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the email");
    } finally { setBusy(false); }
  };

  const openInOutlook = async () => {
    if (!form?.supplier_name?.trim()) return toast.error("Pick or name a supplier first");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/backorders/${backorderId}/supplier-po-email/outlook-draft`,
        payload(), { headers });
      window.open(data.web_link, "_blank", "noopener");
      toast.success("Draft waiting in Outlook", {
        description: "Attach the PO and packing slip, then send.",
      });
      onLogged?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Outlook drafting is unavailable";
      toast.error(detail, { description: "Use “Open in mail app” to compose locally instead." });
      if (!preview) buildPreview();
    } finally { setBusy(false); }
  };

  const openMailto = async () => {
    let p = preview;
    if (!p) {
      try {
        const { data } = await axios.post(
          `${API}/api/admin/backorders/${backorderId}/supplier-po-email/build`,
          payload(), { headers });
        p = data; setPreview(data);
      } catch { return toast.error("Could not build the email"); }
    }
    window.location.href = p.mailto_url;
  };

  const suppliers = ctx?.suppliers || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white"
        data-testid="supplier-po-email-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-700" /> Send supplier PO
          </DialogTitle>
          <DialogDescription>
            Builds the PO email for the awarded supplier and opens it in Outlook — you attach the
            purchase order and packing slip before sending.
          </DialogDescription>
        </DialogHeader>

        {loading || !form ? (
          <div className="py-16 text-center text-slate-500" data-testid="po-email-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* supplier picker */}
            {suppliers.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="po-email-supplier-tabs">
                {suppliers.map((s, i) => (
                  <button key={s.award_id || i} type="button" onClick={() => setIdx(i)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                      i === idx ? "bg-indigo-700 text-white border-indigo-700"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                    data-testid={`po-email-supplier-tab-${i}`}>
                    {s.supplier_name}
                  </button>
                ))}
              </div>
            )}
            {suppliers.length === 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5"
                data-testid="po-email-no-award">
                No supplier has been awarded yet — type the supplier name and email below to send
                the PO anyway.
              </p>
            )}

            {/* header fields */}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Supplier name *">
                <Input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)}
                  data-testid="po-email-supplier-name" />
              </Field>
              <Field label="Greeting name (first name)">
                <Input value={form.greeting_name} onChange={(e) => set("greeting_name", e.target.value)}
                  placeholder="Tony" data-testid="po-email-greeting" />
              </Field>
              <Field label="To (comma separated)">
                <Input value={form.to} onChange={(e) => set("to", e.target.value)}
                  data-testid="po-email-to" />
              </Field>
              <Field label="CC (optional)">
                <Input value={form.cc} onChange={(e) => set("cc", e.target.value)}
                  data-testid="po-email-cc" />
              </Field>
              <Field label="Our supplier PO # (subject line)">
                <Input value={form.supplier_po_number}
                  onChange={(e) => set("supplier_po_number", e.target.value)}
                  placeholder="SM-PO-10042" data-testid="po-email-supplier-po" />
              </Field>
              <Field label="Hospital PO #">
                <Input value={form.hospital_po_number}
                  onChange={(e) => set("hospital_po_number", e.target.value)}
                  data-testid="po-email-hospital-po" />
              </Field>
            </div>

            {/* line items */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Line items on this PO
                </p>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setForm((p) => ({ ...p, items: [...p.items, blankLine()] }))}
                  data-testid="po-email-add-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1.1fr_2fr_0.8fr_0.7fr_0.9fr_1fr_28px] gap-1 bg-slate-100
                  px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <span>Product code</span><span>Description</span><span>Qty</span><span>UOM</span>
                  <span>Unit price</span><span>Lead time</span><span />
                </div>
                {form.items.map((l, i) => (
                  <div key={i} className="grid grid-cols-[1.1fr_2fr_0.8fr_0.7fr_0.9fr_1fr_28px] gap-1
                    px-2 py-1.5 border-t border-slate-100" data-testid={`po-email-line-${i}`}>
                    <Input className="h-8 text-xs" value={l.code}
                      onChange={(e) => setLine(i, "code", e.target.value)}
                      data-testid={`po-email-line-code-${i}`} />
                    <Input className="h-8 text-xs" value={l.description}
                      onChange={(e) => setLine(i, "description", e.target.value)}
                      data-testid={`po-email-line-desc-${i}`} />
                    <Input className="h-8 text-xs" value={l.quantity}
                      onChange={(e) => setLine(i, "quantity", e.target.value)}
                      data-testid={`po-email-line-qty-${i}`} />
                    <Input className="h-8 text-xs" value={l.uom}
                      onChange={(e) => setLine(i, "uom", e.target.value)} />
                    <Input className="h-8 text-xs" value={l.unit_price}
                      onChange={(e) => setLine(i, "unit_price", e.target.value)}
                      data-testid={`po-email-line-price-${i}`} />
                    <Input className="h-8 text-xs" value={l.lead_time}
                      placeholder="5/4/2026"
                      onChange={(e) => setLine(i, "lead_time", e.target.value)}
                      data-testid={`po-email-line-lead-${i}`} />
                    <button type="button" className="text-slate-400 hover:text-rose-600"
                      onClick={() => setForm((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}
                      data-testid={`po-email-remove-line-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* instructions */}
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Shipping instructions in the email
              </p>
              {OPTION_ROWS.map((o) => (
                <label key={o.k} className="flex items-start gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={!!form.options[o.k]}
                    onChange={(e) => setOpt(o.k, e.target.checked)}
                    data-testid={`po-email-opt-${o.k}`} className="mt-0.5" />
                  {o.label}
                </label>
              ))}
              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                <Field label="Hospital FedEx account # (blank = omit)">
                  <Input value={form.options.freight_account || ""}
                    onChange={(e) => setOpt("freight_account", e.target.value)}
                    placeholder="304488352" data-testid="po-email-fedex-account" />
                </Field>
                <Field label="Heavy-freight phone">
                  <Input value={form.options.freight_phone || ""}
                    onChange={(e) => setOpt("freight_phone", e.target.value)}
                    data-testid="po-email-fedex-phone" />
                </Field>
              </div>
              <Field label="Anything else to add (optional)">
                <Textarea rows={2} value={form.extra_note}
                  onChange={(e) => set("extra_note", e.target.value)}
                  data-testid="po-email-extra-note" />
              </Field>
              <Field label="Sign off as">
                <Input value={form.sign_off_name} onChange={(e) => set("sign_off_name", e.target.value)}
                  data-testid="po-email-signoff" />
              </Field>
            </div>

            {/* preview */}
            {preview && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3"
                data-testid="po-email-preview">
                <p className="text-[11px] font-bold text-indigo-900 mb-2">
                  Subject: <span className="font-normal">{preview.subject}</span>
                </p>
                <div className="bg-white rounded border border-slate-200 p-3 max-h-72 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: preview.body_html }} />
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" onClick={openInOutlook} disabled={busy}
                className="bg-indigo-700 hover:bg-indigo-800" data-testid="po-email-outlook-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <ExternalLink className="w-4 h-4 mr-1.5" />}
                Open draft in Outlook
              </Button>
              <Button type="button" variant="outline" onClick={openMailto} disabled={busy}
                data-testid="po-email-mailto-btn">
                <Mail className="w-4 h-4 mr-1.5" /> Open in mail app
              </Button>
              <Button type="button" variant="ghost" onClick={buildPreview} disabled={busy}
                data-testid="po-email-preview-btn">
                Preview email
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">
              The draft lands in your Outlook Drafts folder with the table and instructions already
              written — attach the purchase order and packing slip, then send.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
      {label}
    </label>
    {children}
  </div>
);

export default SupplierPoEmailDialog;
